import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import PollIcon from '@mui/icons-material/Poll';
import { FormControl } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DOMPurify from 'dompurify';
import { useSnackbar } from 'notistack';

import MyLoader from '../../components/MyLoader/MyLoader';
import {
  fetchSurveyDemographic,
  fetchSurveyDemographic2,
  fetchSurveyForAnswer,
  fetchSurveyForAnswerPersonal,
  fetchSurveyQuestion,
  selectCurrentSurveyForAnswer,
  selectSurveyDemograpichs2,
  selectSurveyQuestion,
  selectSurveysErrorResponse,
  selectSurveysStatus,
  storeSurvey,
} from '../../features/surveys/surveysSlice';
import { postAnswerSessionAPI } from '../../services/answerSession.service';
import { redeemJarvisReviewAPI } from '../../services/jarvisReview.service';
import client from '../../utils/axiosInstance';
import {
  isMaxAnswersError,
  isMaxAnswersPayload,
} from '../../utils/maxAnswersError';

import MaxAnswersMessage from './components/MaxAnswersMessage/MaxAnswersMessage';
import NotExclusiviness from './components/NotExclusiviness/NotExclusiviness';
import NotFoundMessage from './components/NotFoundMessage/NotFoundMessage';
import SuccessMessage from './components/SuccessMessage/SuccessMessage';
import SurveyForm from './components/SurveyForm/SurveyForm';
import { buildSelectionsFromAnswers } from './utils/demographicUtils';

import 'quill/dist/quill.snow.css';
import styles from './AnswerSurvey.module.css';

/**
 * Answer survey page.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const AnswerSurvey = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [surveyInitialized, setSurveyInitialized] = useState(false);
  const token = useSelector((state) => state.token360.value);
  const { surveyId, companyId, answerId } = useParams();
  const [searchParams] = useSearchParams();
  const rt = searchParams.get('rt');
  const rtRedeemed = useRef(false);
  const RECAPTCHA_SITE_KEY = '6LdKznMtAAAAAHe4cZ8FuIBZQxGS1K_niaWh2ace';
  const [captchaOk, setCaptchaOk] = useState(false);

  // Verifica el captcha (invisible) contra el server y guarda la sesión.
  const handleAnswerCaptcha = async (captchaToken) => {
    if (!captchaToken) return;
    try {
      const { data } = await postAnswerSessionAPI(surveyId, captchaToken);
      sessionStorage.setItem('answerSession', data.sessionToken);
      setCaptchaOk(true);
    } catch (e) {
      setCaptchaOk(false);
    }
  };
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [openDeclineModal, setOpenDeclineModal] = useState(false);
  const [emailBuzon, setEmailBuzon] = useState('');
  const [steps, setSteps] = useState([
    'Datos demográficos',
    'Encuesta',
    'Finalizar',
  ]);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [stepsCompleted, setStepsCompleted] = useState([false, false, true]);
  const [answers, setAnswers] = useState([[], {}]);
  const [answerIdAPI, setAnswerIdAPI] = useState('');
  const [demographicUserData, setDemographicUserData] = useState(null);
  const [isPersonal, setIsPersonal] = useState(false);
  const [pausedData, setPausedData] = useState(null);
  const [isPausedSurvey, setIsPausedSurvey] = useState(false);
  const surveyStatus = useSelector((state) => selectSurveysStatus(state));
  const [exclusiviness, setExclusiviness] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState(false);
  const [inputErrorMessage, setInputErrorMessage] = useState('');
  const currentSurvey = useSelector((state) =>
    selectCurrentSurveyForAnswer(state),
  );
  const currentSurveyQuestion = useSelector((state) =>
    selectSurveyQuestion(state),
  );

  const currentSurveyDemographic = useSelector((state) =>
    selectSurveyDemograpichs2(state),
  );
  const answersRef = useRef(answers);
  const hasInitialized = useRef(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(true);
  const [isAlreadyResponse, setIsAlreadyResponse] = useState(false);
  // Se alcanzo el tope de Survey.MaxAnswer (409 del backend). Se distingue del
  // 409 de "ya respondiste" porque el mensaje que ve la persona es otro.
  const [maxAnswersReached, setMaxAnswersReached] = useState(false);
  const surveysErrorResponse = useSelector((state) =>
    selectSurveysErrorResponse(state),
  );
  const dispatch = useDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * Función para obtener datos pausados
   * @param {string} sessionId - ID de la sesión
   */
  // Demográficos de una encuesta retomada, en el formato que entiende el
  // formulario. El servidor los guarda al pausar y los devuelve al reanudar,
  // pero el formulario se pintaba solo desde sessionStorage -que muere al
  // cerrar la pestaña-, así que quien volvía después los veía vacíos y tenía
  // que llenarlos otra vez.
  //
  // Se deja aparte de demographicUserData a propósito: eso último tiene
  // prioridad al armar el envío, y usarlo aquí haría que, si la persona
  // cambia algo al retomar, se mandaran igual los valores viejos.
  const initialDemographicSelections = useMemo(() => {
    const guardados = pausedData?.demographics;
    const arbol = currentSurveyDemographic?.demographics;
    if (
      !Array.isArray(guardados) ||
      guardados.length === 0 ||
      !Array.isArray(arbol) ||
      arbol.length === 0
    ) {
      return null;
    }

    const normalizado = arbol.map((demografico) => ({
      demographicName: demografico.name || demografico.demographicName,
      levelNames: demografico.levelNames,
      nodes: demografico.nodes,
      allowMultipleSelection: demografico.allowMultipleSelection,
    }));

    // El progreso los devuelve como {id, value}; aquí se usa {name, value}.
    const respuestas = guardados.map((d) => ({
      name: d.name ?? d.id,
      value: d.value,
    }));

    return buildSelectionsFromAnswers(respuestas, normalizado);
  }, [pausedData, currentSurveyDemographic]);

  const fetchPausedData = async (sessionId) => {
    try {
      const pausedResponse = await client.get(
        `GetPausedBySession/${sessionId}`,
      );
      setPausedData(pausedResponse.data);
      return pausedResponse.data;
    } catch (pausedError) {
      console.error('Error al obtener datos pausados:', pausedError);
      return null;
    }
  };

  /**
   * Handle email change.
   *
   * @param event
   */
  const handleEmailChange = (event) => {
    event.preventDefault();
    setEmail(event.target.value);
  };

  const handlePreviousAnswer = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };
  /**
   * Handle input change con validación
   */
  const handleInputChange = (event) => {
    const value = event.target.value;
    const textType = currentSurvey?.response?.settings?.textType;

    // Si es tipo number, solo permitir números
    if (textType === 'number') {
      // Solo permitir dígitos
      if (value === '' || /^\d+$/.test(value)) {
        setInputValue(value);
        setInputError(false);
        setInputErrorMessage('');
      }
    } else {
      setInputValue(value);
      setInputError(false);
      setInputErrorMessage('');
    }
  };
  /**
   * Validar el input según el tipo
   */
  const validateInput = (value, type) => {
    if (!value.trim()) {
      return { valid: false, message: 'Este campo es obligatorio' };
    }

    switch (type) {
      case 'number':
        if (!/^\d+$/.test(value)) {
          return { valid: false, message: 'Solo se permiten números' };
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return {
            valid: false,
            message: 'Ingrese un correo electrónico válido',
          };
        }
        break;
      case 'text':
      default:
        // Para texto, aceptar cualquier cosa
        break;
    }

    return { valid: true, message: '' };
  };
  /**
   * Handle email submit.
   *
   * @param event
   */

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    const textType = currentSurvey?.response?.settings?.textType || 'text';
    const validation = validateInput(inputValue, textType);

    if (!validation.valid) {
      setInputError(true);
      setInputErrorMessage(validation.message);
      return;
    }

    try {
      const url = currentSurvey?.response?.isBuzon
        ? `getMailPersonalSurvey/${surveyId}/${companyId}/${inputValue}/${emailBuzon}`
        : `getMailPersonalSurvey/${surveyId}/${companyId}/${inputValue}`;
      const response = await client.get(url);
      const { data } = response;

      setAnswerIdAPI(data.id);

      if (data?.isPaused) {
        await fetchPausedData(data.id);
      }

      if (data && data.demographics) {
        setDemographicUserData(data.demographics);
        setSteps((prevSteps) => {
          const newSteps = [...prevSteps];
          newSteps.splice(0, 1);
          return newSteps;
        });
      }
      setEmailSubmitted(true);
    } catch (error) {
      if (isMaxAnswersError(error)) {
        setNotFound(false);
        setMaxAnswersReached(true);
      } else if (error.response && error.response.status === 409) {
        setSteps((prevSteps) => {
          const newSteps = [...prevSteps];
          newSteps.splice(0, 1);
          return newSteps;
        });
        setNotFound(false);
        setEmailSubmitted(true);
        setIsAlreadyResponse(true);
      } else if (error.response && error.response.status === 404) {
        setSteps((prevSteps) => {
          const newSteps = [...prevSteps];
          newSteps.splice(0, 1);
          return newSteps;
        });
        setExclusiviness(true);
      } else {
        console.error('Se produjo un error al hacer la solicitud', error);
      }
    }
  };

  /**
   * Returns true if the step is optional.
   *
   * @param step
   * @returns {boolean}
   */
  const isStepOptional = (step) => {
    return false;
  };

  /**
   * Returns true if the step is skipped.
   *
   * @param step
   * @returns {boolean}
   */
  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  /**
   * Returns true if is the privacy policy step.
   *
   * @returns {boolean}
   */
  const isPrivacyStep = () => {
    return steps[0] === 'Políticas de Privacidad' && activeStep === 0;
  };

  /**
   * Returns true if is the introduction step.
   *
   * @returns {boolean}
   */
  const isIntroductionStep = () => {
    const introductionIndex = steps.indexOf('Introducción');
    return activeStep === introductionIndex && introductionIndex !== -1;
  };

  /**
   * Returns true if is the demographic step.
   *
   * @returns {boolean}
   */
  const isDemographicStep = () => {
    const demographicIndex = steps.indexOf('Datos demográficos');
    return activeStep === demographicIndex && demographicIndex !== -1;
  };

  /**
   * Returns true if is the survey step.
   *
   * @returns {boolean}
   */
  const isSurveyStep = () => {
    const surveyIndex = steps.indexOf('Encuesta');
    return activeStep === surveyIndex && surveyIndex !== -1;
  };

  /**
   * Returns true if the step is the last one.
   *
   * @returns {boolean}
   */
  const isFinalStep = () => {
    return activeStep === steps.length - 1;
  };

  const uploadAnswerFile = async (file, questionId) => {
    const formData = new FormData();
    formData.append('surveyId', surveyId); // ya lo tienes del useParams
    formData.append('questionId', questionId);
    formData.append('file', file);

    const response = await client.post('/upload/file/answer', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data.url; // o como devuelva la URL tu backend
  };
  /**
   * Handle next step.
   */
  const handleNext = async () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    // store answers
    if (steps.length - activeStep === 2) {
      // demographic step

      // ✅ Determinar si hay demographics en el flujo
      const hasDemographics = steps.indexOf('Datos demográficos') !== -1;

      // ✅ Obtener las respuestas correctas
      //let rawAnswers = hasDemographics ? answers[1] : answers[0];
      let rawAnswers = hasDemographics
        ? answersRef.current[1]
        : answersRef.current[0];
      console.log(
        'rawAnswers value pregunta Archivos:',
        JSON.stringify(
          rawAnswers?.find((a) => a.questionType === 'Archivos')?.value,
        ),
      );
      // ✅ Asegurar que sea un array antes de filtrar
      const answersArray = Array.isArray(rawAnswers) ? rawAnswers : [];

      // ✅ Filtrar respuestas de preguntas de tipo "Archivos"
      const answersWithFiles = await Promise.all(
        answersArray.map(async (answer) => {
          const question = currentSurveyQuestion?.find(
            (q) => q.questionId === answer.id,
          );

          if (question?.typeQuestion === 'Archivos') {
            const files = answer.value;

            // ✅ Si ya es string con URLs, no transformar
            if (typeof files === 'string' && files.length > 0) return answer;

            if (!Array.isArray(files) || files.length === 0) {
              return { ...answer, value: '' };
            }

            // ✅ Subir archivos y obtener URLs
            const uploadedUrls = await Promise.all(
              files.map(async (file) => {
                if (!(file instanceof File)) return file;
                return uploadAnswerFile(file, answer.id);
              }),
            );

            return { ...answer, value: uploadedUrls.join(', ') };
          }

          return answer;
        }),
      );

      // ✅ Construir demographics
      let demographics = [];

      if (demographicUserData !== null) {
        // Si hay demographicUserData (usuario identificado con datos precargados)
        demographics = Array.isArray(demographicUserData)
          ? demographicUserData
          : [demographicUserData];
      } else if (hasDemographics) {
        // Si hay datos demográficos en el formulario, siempre están en answers[0]
        const demData = answers[0];
        demographics = Array.isArray(demData) ? demData : [demData];
      }

      const payload = {
        surveyId: Number.parseInt(surveyId),
        answerId: answerId ?? answerIdAPI ?? null,
        demographics: demographics, // ✅ Datos correctos
        answers: answersWithFiles, // ✅ Usar respuestas filtradas
        isPolicyAccepted: privacyAccepted ? true : undefined,
      };

      // ✅ Caso especial: usuario personal con datos precargados
      if (demographicUserData !== null) {
        if (
          typeof demographicUserData === 'object' &&
          !Array.isArray(demographicUserData)
        ) {
          payload.demographics = [demographicUserData];
        } else if (Array.isArray(demographicUserData)) {
          payload.demographics = demographicUserData;
        }

        // ✅ También filtrar aquí con validación de array
        //const rawPersonalAnswers = answers[0];
        const rawPersonalAnswers = answersWithFiles;
        const personalAnswersArray = Array.isArray(rawPersonalAnswers)
          ? rawPersonalAnswers
          : [];

        const filteredPersonalAnswers = personalAnswersArray.map((answer) => {
          const question = currentSurveyQuestion?.find(
            (q) => q.questionId === answer.id,
          );
          if (question?.typeQuestion === 'Archivos') {
            const files = answer.value;
            // ✅ Si ya es string, ya tiene URLs — no transformar
            if (typeof files === 'string' && files.length > 0) return answer;

            if (!Array.isArray(files) || files.length === 0) {
              return { ...answer, value: '' };
            }
          }
          return answer;
        });

        payload.answers = filteredPersonalAnswers;
      }

      try {
        await dispatch(storeSurvey(payload)).unwrap();
      } catch (error) {
        if (isMaxAnswersPayload(error)) {
          setNotFound(false);
          setMaxAnswersReached(true);
        } else if (error?.status === 409) {
          setSteps((prevSteps) => {
            const newSteps = [...prevSteps];
            newSteps.splice(0, 1);
            return newSteps;
          });
          setNotFound(false);
          setEmailSubmitted(true);
          setIsAlreadyResponse(true);
        } else {
          console.error('Error:', error);
        }
      }

      localStorage.setItem('formValues', JSON.stringify([]));
      localStorage.removeItem('demographicSelections');
    }

    if (activeStep + 1 === steps.length) {
      window.close();
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  /**
   * Handle back step.
   */
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  /**
   * Handle skip step.
   */
  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error('You can\'t skip a step that isn\'t optional.');
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  /**
   * Handle change on answers.
   *
   * @param answers
   * @param step
   */
  /**
   * Handle change on answers.
   *
   * @param answers
   * @param step
   */
  /**
   * Obtiene el índice real de answers basado en el step actual
   */
  const getAnswerIndex = useCallback(
    (currentStep) => {
      let index = currentStep;

      // Restar los steps que no guardan respuestas reales
      const privacyIndex = steps.indexOf('Políticas de Privacidad');
      if (privacyIndex !== -1 && currentStep > privacyIndex) {
        index--;
      }

      const introIndex = steps.indexOf('Introducción');
      if (introIndex !== -1 && currentStep > introIndex) {
        index--;
      }

      const finalIndex = steps.indexOf('Finalizar');
      if (finalIndex !== -1 && currentStep >= finalIndex) {
        index--;
      }

      return index;
    },
    [steps],
  );

  /**
   * Handle change on answers.
   */
  const handleAnswered = useCallback(
    (answersData, step) => {
      setAnswers((prevAnswers) => {
        const newAnswers = [...prevAnswers];
        const answerIndex = getAnswerIndex(step); // ✅ Usar helper
        newAnswers[answerIndex] = answersData;
        answersRef.current = newAnswers;
        return newAnswers;
      });

      setStepsCompleted((prevStepsCompleted) => {
        const newStepsCompleted = [...prevStepsCompleted];
        newStepsCompleted[step] = true;

        answersData.forEach(({ value, values }) => {
          if (Object.keys(values).length === 0 && !value) {
            newStepsCompleted[step] = false;
            return false;
          }
        });

        return newStepsCompleted;
      });
    },
    [getAnswerIndex],
  );
  /**
   * Check if is personal survey.
   *
   * @returns {Promise<any>}
   */
  const checkIfIsPersonal = async () => {
    setLoading(true);

    const { data: isPersonal } = await client.get(
      `ValidateAnswerSurvey/${surveyId}/${companyId}`,
    );
    if (!isPersonal) {
      setEmailSubmitted(true);
    }
    setIsPersonal(isPersonal);
    setLoading(false);
  };

  const saveSurvey = async () => {
    try {
      // ✅ Determinar si hay demographics en el flujo
      const hasDemographics = steps.indexOf('Datos demográficos') !== -1;

      // ✅ Construir demographics
      let demographics = [];

      if (demographicUserData !== null) {
        // Si hay demographicUserData (usuario identificado con datos precargados)
        demographics = Array.isArray(demographicUserData)
          ? demographicUserData
          : [demographicUserData];
      } else if (hasDemographics) {
        // Si hay datos demográficos en el formulario, siempre están en answers[0]
        const demData = answers[0];
        demographics = Array.isArray(demData) ? demData : [demData];
      }

      const payload = {
        surveyId: Number.parseInt(surveyId),
        answerId: isPersonal ? answerId || answerIdAPI : null,
        demographics: demographics, // ✅ Datos correctos
        // Survey siempre está en answers[1] si hay demographics, o en answers[0] si no los hay
        answers: hasDemographics ? answers[1] : answers[0],
        isPolicyAccepted: pausedData?.hasAcceptedPrivacyPolicy
          ? pausedData?.hasAcceptedPrivacyPolicy
          : privacyAccepted,
      };

      await client.post('/saveQuestionInPause', payload);

      enqueueSnackbar('Encuesta guardada exitosamente', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    } catch (error) {
      enqueueSnackbar('Error al guardar la encuesta. Inténtalo de nuevo.', {
        variant: 'error',
        autoHideDuration: 5000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });

      console.error('Error saving survey:', error);
    }
  };

  /**
   * Abrir modal de confirmación de rechazo
   */
  const handleDeclinePrivacyClick = () => {
    setOpenDeclineModal(true);
  };

  /**
   * Cerrar modal sin hacer nada (cancelar)
   */
  const handleCancelDecline = () => {
    setOpenDeclineModal(false);
  };

  /**
   * Confirmar que no acepta las políticas
   */
  const handleConfirmDecline = async () => {
    try {
      // Consumir endpoint de rechazo
      const data = answerId ?? answerIdAPI ?? null;
      if (data) {
        await client.post(`Policies/${data}`);
      }
      enqueueSnackbar('Has rechazado las políticas de privacidad', {
        variant: 'info',
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });

      // Ir al último paso (Finalizar)
      setActiveStep(steps.length - 1);
      setOpenDeclineModal(false);
    } catch (error) {
      enqueueSnackbar('Error al procesar el rechazo. Inténtalo de nuevo.', {
        variant: 'error',
        autoHideDuration: 5000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });

      console.error('Error al rechazar políticas:', error);
      setOpenDeclineModal(false);
    }
  };

  /**
   * Handle decline privacy policy.
   */
  const handleDeclinePrivacy = () => {
    // Ir directamente al último paso (Finalizar)
    setActiveStep(steps.length - 1);
  };

  function parsePrivacyPolicyText(html, url) {
    if (!html || !url) return html;

    const markerRegex = /(?:<<<|&lt;&lt;&lt;)(.*?)(?:>>>|&gt;&gt;&gt;)/g;

    // Caso 1: hay marcador → reemplazarlo por link
    if (markerRegex.test(html)) {
      return html.replace(
        markerRegex,
        `<a href="${url}" target="_blank" rel="noopener noreferrer">$1</a>`,
      );
    }

    // Caso 2: NO hay marcador → agregar link al final
    return html.replace(
      /<\/p>\s*$/,
      ` <a href="${url}" target="_blank" rel="noopener noreferrer">
        Ver políticas de privacidad
      </a></p>`,
    );
  }
  // watch surveyId and companyId changes
  useEffect(() => {
    if (answerId) {
      dispatch(
        fetchSurveyForAnswerPersonal({ surveyId, companyId, answerId }),
      ).then((result) => {
        if (isMaxAnswersPayload(result.payload)) {
          setMaxAnswersReached(true);
          setNotFound(false);
        } else if (result.payload?.status === 409) {
          setIsAlreadyResponse(true);
          setNotFound(false);
        } else if (result.payload?.response?.isPaused && answerId) {
          fetchPausedData(answerId);
        }
      });
    } else {
      dispatch(fetchSurveyForAnswer({ surveyId, companyId }));
    }
  }, [surveyId, companyId, answerId]);

  // El tope de respuestas tambien se valida al servir las preguntas, que es por
  // donde entran las encuestas anonimas (haveUser === false): ahi el 409 no pasa
  // por handleEmailSubmit sino por el store.
  useEffect(() => {
    if (isMaxAnswersPayload(surveysErrorResponse)) {
      setNotFound(false);
      setMaxAnswersReached(true);
    }
  }, [surveysErrorResponse]);

  // watch currentSurvey changes
  useEffect(() => {
    if (
      (currentSurveyDemographic &&
        currentSurveyDemographic.demographics === null) ||
      hasInitialized.current
    ) {
      return;
    }

    // if demographic data is not required, remove the first step
    if (
      currentSurveyDemographic &&
      currentSurveyDemographic.demographics.length === 0
    ) {
      setSteps((prevSteps) => prevSteps.slice(1));
      setStepsCompleted((prevCompleted) => prevCompleted.slice(1));
      setAnswers((prevAnswers) => prevAnswers.slice(1));
    }

    hasInitialized.current = true;
  }, [currentSurveyDemographic]);

  // component did mount
  useEffect(() => {
    if (!currentSurvey?.response) return;

    const requiresIdentification = currentSurvey.response.haveUser === true;

    if (requiresIdentification) {
      checkIfIsPersonal();
    }

    // Jarvis (?rt=) carga vía answerIdAPI (accessId); no pasa por captcha.
    if (rt) return;

    // Humano: exige captcha (sesión) antes de cargar contenido.
    if (!captchaOk) return;

    // - answerId en la URL (link personal / pausado / 360), o
    // - encuesta anónima (no requiere identificación).
    // Si requiere identificación y aún no hay id, se difiere hasta answerIdAPI.
    if (answerId) {
      dispatch(fetchSurveyQuestion({ surveyId, companyId, answerId }));
      dispatch(fetchSurveyDemographic2({ surveyId, answerId }));
    } else if (!requiresIdentification) {
      dispatch(fetchSurveyQuestion({ surveyId, companyId }));
      dispatch(fetchSurveyDemographic2({ surveyId }));
    }
  }, [currentSurvey, captchaOk]);

  // Se cargan preguntas/demográficos con el id: humano identificado
  // (getMailPersonalSurvey → answerIdAPI, con captcha) o Jarvis (accessId, con rt).
  useEffect(() => {
    if (answerId) return; // ya se cargó por la rama de URL
    if (!answerIdAPI) return;
    if (!rt && !captchaOk) return; // humano requiere captcha; Jarvis (rt) no

    dispatch(fetchSurveyQuestion({ surveyId, companyId, answerId: answerIdAPI }));
    dispatch(fetchSurveyDemographic2({ surveyId, answerId: answerIdAPI }));
  }, [answerIdAPI, captchaOk]);

  // Acceso de revisión de Jarvis: si viene ?rt=, canjear el token una sola vez.
  useEffect(() => {
    if (!rt || rtRedeemed.current) return;
    rtRedeemed.current = true;
    redeemJarvisReviewAPI(rt)
      .then(({ data }) => {
        setAnswerIdAPI(data.accessId);
        setEmailSubmitted(true);
      })
      .catch(() => {
        setNotFound(true);
      });
  }, [rt]);

  // Captcha invisible (reCAPTCHA v3 por score): al entrar (no Jarvis, sin sesión aún)
  // ejecuta el reto y obtiene la sesión de respuesta.
  useEffect(() => {
    if (rt || captchaOk) return;
    if (!currentSurvey?.response) return;
    const SRC = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    const run = () => {
      window.grecaptcha?.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: 'answer' })
          .then(handleAnswerCaptcha)
          .catch(() => {});
      });
    };
    if (window.grecaptcha) {
      run();
      return;
    }
    let s = document.querySelector(`script[src="${SRC}"]`);
    if (!s) {
      s = document.createElement('script');
      s.src = SRC;
      s.async = true;
      s.defer = true;
      s.onload = run;
      document.head.appendChild(s);
    } else {
      s.addEventListener('load', run);
    }
  }, [currentSurvey, captchaOk, rt]);

  // Reemplaza los dos useEffect separados por este único:
  useEffect(() => {
    if (!currentSurvey?.response) return;

    const hasPrivacyPolicy =
      currentSurvey.response.isPrivacyPolicyEnabled ||
      currentSurvey.response.privacySettings?.dataPrivacyPolicyText ||
      currentSurvey.response.privacySettings?.isEnabled;

    const hasIntroductionSurvey =
      currentSurvey?.response?.settings?.hasIntroduction &&
      currentSurvey?.response?.settings?.introductionText &&
      currentSurvey.response.settings.introductionText.trim().length > 0;

    const hasDemographics =
      currentSurveyDemographic &&
      currentSurveyDemographic.demographics &&
      currentSurveyDemographic.demographics.length > 0;
    const hasAcceptedPrivacyFromPause =
      pausedData?.hasAcceptedPrivacyPolicy === true;
    // Construir los steps desde cero basándose en las condiciones
    let newSteps = [];
    let newStepsCompleted = [];
    let newAnswers = [];

    // 1. Agregar políticas si está habilitado
    if (hasPrivacyPolicy && !hasAcceptedPrivacyFromPause) {
      newSteps.push('Políticas de Privacidad');
      newStepsCompleted.push(false);
    }

    // 2. Agregar políticas si está habilitado
    if (hasIntroductionSurvey) {
      newSteps.push('Introducción');
      newStepsCompleted.push(false);
    }

    // 3. Agregar demográficos si hay datos
    if (hasDemographics) {
      newSteps.push('Datos demográficos');
      newStepsCompleted.push(false);
    }

    // 4. Siempre agregar Encuesta y Finalizar
    newSteps.push('Encuesta');
    newStepsCompleted.push(false);

    newSteps.push('Finalizar');
    newStepsCompleted.push(true);

    setSteps(newSteps);
    setStepsCompleted(newStepsCompleted);
  }, [currentSurvey, currentSurveyDemographic, pausedData]);

  const hasOwnBold = (html) => /<strong|<b[\s>]/i.test(html);
  return (
    <div
      className={styles.AnswerSurvey}
      style={{
        backgroundImage: currentSurvey?.response?.settings?.urlBackground
          ? `url(${currentSurvey.response.settings?.urlBackground})`
          : 'none',
        backgroundSize: 'cover', // ajusta a tu gusto (cover, contain, etc.)
        backgroundPosition: 'center', // centra la imagen
        backgroundColor:
          !currentSurvey?.response?.settings?.urlBackground &&
          currentSurvey?.response?.settings?.secondaryColor,
      }}
      translate="no"
    >
      <div
        className={styles.AnswerSurvey__Background}
        style={{
          backgroundColor: currentSurvey?.response?.settings?.primaryColor,
          backgroundImage: currentSurvey?.response?.settings?.urlBanner
            ? `url(${currentSurvey.response.settings?.urlBanner})`
            : 'none',
          backgroundSize: currentSurvey?.response?.settings?.urlBanner
            ? '80% auto' // la imagen ocupa el 80% del ancho
            : 'cover', // si no hay banner, que el color ocupe todo
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      ></div>
      <div
        className={styles.AnswerSurvey__Content}
        style={{
          marginTop: currentSurvey?.response?.settings?.urlBanner
            ? '15vh'
            : '0',
          transition: 'margin-top 0.3s ease',
        }}
      >
        <Card
          sx={{
            minWidth: 300,
            maxWidth: 800,
          }}
        >
          <CardContent>
            {surveyStatus === 'loading' && (
              <MyLoader
                color={
                  currentSurvey && currentSurvey.response.settings?.primaryColor
                }
              />
            )}

            {isAlreadyResponse && !exclusiviness && !maxAnswersReached && (
              <SuccessMessage
                isAlreadyResponse={isAlreadyResponse}
                isView360={currentSurvey && currentSurvey.response.isView360}
                surveyId={surveyId}
                companyId={companyId}
                token={token}
                isPersonalLink={Boolean(answerId)}
              />
            )}
            {surveyStatus === 'failed' &&
              notFound &&
              !isAlreadyResponse &&
              !maxAnswersReached && (
                <NotFoundMessage
                  infoMessage={'Lo sentimos esta encuesta no esta disponible :('}
                />
              )}
            {maxAnswersReached && <MaxAnswersMessage />}
            {exclusiviness && !maxAnswersReached && <NotExclusiviness />}
            {surveyStatus === 'succeeded' &&
              currentSurvey !== null &&
              !exclusiviness &&
              !isAlreadyResponse &&
              !maxAnswersReached && (
                <Fragment>
                  {/* company name */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexDirection: {
                        xs: 'column-reverse',
                        sm: 'row',
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      gutterBottom
                      style={{
                        flex: '1 0 65%',
                        fontSize: '25px',
                        fontWeight: 'bold',
                        fontFamily:
                          currentSurvey?.response?.fontFamily || 'Roboto',
                      }}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            currentSurvey?.response?.surveyName,
                          ),
                        }}
                      />
                    </Typography>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: 'fit-content',
                      }}
                    >
                      <div
                        style={{
                          width: '200px',
                          height: '60px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {currentSurvey.logo ? (
                          <img
                            src={currentSurvey.logo}
                            alt="Logotipo de la empresa"
                            style={{
                              maxHeight: '100%',
                              maxWidth: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <PollIcon style={{ fontSize: '40px' }} />
                        )}
                      </div>
                    </div>
                  </Box>
                  <Divider
                    variant="middle"
                    style={{
                      margin: '1.3em 0',
                    }}
                  />

                  {/* email */}
                  {emailSubmitted === false &&
                    !answerId &&
                    currentSurvey &&
                    currentSurvey.response.haveUser && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '3em 0',
                          flexDirection: 'column',
                          fontFamily:
                            currentSurvey?.response?.fontFamily || 'Roboto',
                          '& *': {
                            fontFamily: 'inherit !important', // ✅ Esto FUERZA a todos los hijos
                          },
                        }}
                      >
                        <FormControl style={{ width: '300px' }}>
                          <FormLabel
                            id="email"
                            sx={{
                              marginBottom: '1.3em',
                              fontSize: '1.4em',
                              fontWeight: hasOwnBold(
                                currentSurvey.response.welcomeMessage,
                              )
                                ? 'normal'
                                : 'bold',
                              fontFamily: `${
                                currentSurvey?.response?.fontFamily || 'Roboto'
                              }`,
                            }}
                            error={emailError}
                          >
                            <span
                              className="ql-editor"
                              style={{ padding: 0 }}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  currentSurvey.response.welcomeMessage,
                                ),
                              }}
                            />
                          </FormLabel>

                          <TextField
                            id="email"
                            label={currentSurvey.response.inputMessage}
                            variant="outlined"
                            type={
                              currentSurvey?.response?.settings?.textType ===
                              'number'
                                ? 'text'
                                : currentSurvey?.response?.settings?.textType ||
                                  'text'
                            }
                            value={inputValue}
                            onChange={handleInputChange}
                            error={inputError}
                            helperText={inputError ? inputErrorMessage : ''}
                            inputProps={{
                              ...(currentSurvey?.response?.settings
                                ?.textType === 'number' && {
                                inputMode: 'numeric',
                              }),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor:
                                    currentSurvey?.response?.settings
                                      ?.primaryColor || '#ccc',
                                },
                                '&:hover fieldset': {
                                  borderColor:
                                    currentSurvey?.response?.settings
                                      ?.primaryColor || '#888',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor:
                                    currentSurvey?.response?.settings
                                      ?.primaryColor || '#03aae4',
                                },
                              },
                              '& .MuiInputLabel-root.Mui-focused': {
                                color:
                                  currentSurvey?.response?.settings
                                    ?.primaryColor || '#03aae4',
                              },
                            }}
                          />

                          {currentSurvey?.response?.isBuzon && (
                            <TextField
                              id="email"
                              label={
                                currentSurvey.response.requiredInputMailBox
                              }
                              variant="outlined"
                              type="text"
                              value={emailBuzon}
                              onChange={(e) => setEmailBuzon(e.target.value)}
                              sx={{
                                mt: 2,
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor:
                                      currentSurvey?.response?.settings
                                        ?.primaryColor || '#ccc',
                                  },
                                  '&:hover fieldset': {
                                    borderColor:
                                      currentSurvey?.response?.settings
                                        ?.primaryColor || '#888',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor:
                                      currentSurvey?.response?.settings
                                        ?.primaryColor || '#03aae4',
                                  },
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color:
                                    currentSurvey?.response?.settings
                                      ?.primaryColor || '#03aae4',
                                },
                              }}
                            />
                          )}
                        </FormControl>

                        <FormControl>
                          <Button
                            variant="contained"
                            onClick={handleEmailSubmit}
                            sx={{
                              backgroundColor:
                                currentSurvey?.response?.settings?.primaryColor,
                              color:
                                currentSurvey?.response?.settings
                                  ?.secondaryColor,
                              marginTop: '1em',
                              width: '300px',
                              '&:hover': {
                                backgroundColor:
                                  currentSurvey?.response?.settings
                                    ?.secondaryColor || '#0288d1',
                                color:
                                  currentSurvey?.response?.settings
                                    ?.primaryColor,
                              },
                            }}
                          >
                            Enviar
                          </Button>
                        </FormControl>

                        <Typography
                          variant="body1"
                          sx={{
                            width: '100%',
                            fontStyle: 'italic',
                            fontWeight: hasOwnBold(
                              currentSurvey.response.message,
                            )
                              ? 'normal'
                              : 'bold',
                            color: '#6c6c6c',
                            marginTop: 2,
                          }}
                        >
                          <span
                            className="ql-editor"
                            style={{ padding: 0 }}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                currentSurvey.response.message,
                              ),
                            }}
                          />
                        </Typography>
                      </Box>
                    )}

                  {/* stepper */}
                  {(emailSubmitted === true ||
                    answerId ||
                    !isPersonal ||
                    !currentSurvey.response.haveUser) && (
                    <Fragment>
                      <Stepper
                        style={{
                          marginTop: '2em',
                          fontFamily:
                            currentSurvey?.response?.fontFamily || 'Roboto', // ✅ Agregar aquí
                        }}
                        activeStep={activeStep}
                      >
                        {steps.map((label, index) => {
                          const stepProps = {};
                          const labelProps = {};
                          if (isStepOptional(index)) {
                            labelProps.optional = (
                              <Typography variant="caption">
                                Optional
                              </Typography>
                            );
                          }
                          if (isStepSkipped(index)) {
                            stepProps.completed = false;
                          }
                          return (
                            <Step key={label} {...stepProps}>
                              <StepLabel
                                {...labelProps}
                                sx={{
                                  display: 'flex',
                                  flexDirection:
                                    steps.length === 5 ? 'column' : 'row',
                                  alignItems: 'center',

                                  fontFamily:
                                    currentSurvey?.response?.fontFamily ||
                                    'Roboto',

                                  // 🔑 TEXTO DEL LABEL
                                  '& .MuiStepLabel-label': {
                                    fontFamily: 'inherit !important',

                                    display:
                                      isMobile && index !== activeStep
                                        ? 'none'
                                        : 'block',

                                    textAlign: 'center',
                                    marginTop: steps.length === 5 ? '0.5em' : 0,
                                  },

                                  '& .MuiStepIcon-root': {
                                    color: '#cfcfcf',
                                  },

                                  '& .MuiStepIcon-text': {
                                    fill: 'black',
                                    fontFamily: 'inherit !important',
                                  },

                                  '& .Mui-active .MuiStepIcon-root': {
                                    color:
                                      currentSurvey?.response?.settings
                                        ?.primaryColor,
                                  },

                                  '& .Mui-active .MuiStepIcon-text': {
                                    fill: currentSurvey?.response?.settings
                                      ?.secondaryColor,
                                  },

                                  '& .Mui-completed .MuiStepIcon-root': {
                                    color:
                                      currentSurvey?.response?.settings
                                        ?.primaryColor,
                                  },

                                  '& .Mui-completed .MuiStepIcon-text': {
                                    fill: currentSurvey?.response?.settings
                                      ?.secondaryColor,
                                  },
                                }}
                              >
                                {label}
                              </StepLabel>
                            </Step>
                          );
                        })}
                      </Stepper>
                      <React.Fragment>
                        {/* Privacy Policy Step */}
                        {isPrivacyStep() &&
                          !pausedData?.hasAcceptedPrivacyPolicy && (
                            <Fragment>
                              <Box
                                sx={{
                                  padding: '1em',
                                  fontFamily:
                                    currentSurvey?.response?.fontFamily ||
                                    'Roboto',
                                  '& *': {
                                    // ✅ Esto fuerza la fuente a TODOS los elementos hijos
                                    fontFamily: 'inherit !important',
                                  },
                                }}
                              >
                                {/* Texto de políticas */}
                                <Box
                                  sx={{
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    padding: '1em',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    marginBottom: '1.5em',
                                    border: '1px solid #e0e0e0',
                                    lineHeight: 1.6,
                                    '& p': { margin: '0.5em 0' },
                                    '& ul, & ol': { paddingLeft: '1.5em' },
                                    '& a': {
                                      color:
                                        currentSurvey?.response?.settings
                                          ?.primaryColor || '#03aae4',
                                    },
                                    '& h1, & h2, & h3': { margin: '0.5em 0' },
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: parsePrivacyPolicyText(
                                      currentSurvey?.response?.privacySettings
                                        ?.dataPrivacyPolicyText || '',
                                      currentSurvey?.response?.privacySettings
                                        ?.dataPrivacyPolicyUrl || '',
                                    ),
                                  }}
                                />

                                {/* Link a políticas externas */}
                                {currentSurvey?.response?.privacySettings
                                  ?.urlPrivacy && (
                                  <Box
                                    sx={{
                                      marginBottom: '1.5em',
                                      textAlign: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Para más información, consulte nuestras{' '}
                                      <a
                                        href={
                                          currentSurvey.response.privacySettings
                                            ?.urlPrivacy
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color:
                                            currentSurvey?.response?.settings
                                              ?.primaryColor || '#03aae4',
                                          textDecoration: 'underline',
                                        }}
                                      >
                                        políticas de privacidad
                                      </a>
                                    </Typography>
                                  </Box>
                                )}

                                {/* Checkbox de lectura */}
                                <FormControl
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={privacyAccepted}
                                        onChange={(e) =>
                                          setPrivacyAccepted(e.target.checked)
                                        }
                                        sx={{
                                          color:
                                            currentSurvey?.response?.settings
                                              ?.primaryColor || '#03aae4',
                                          '&.Mui-checked': {
                                            color:
                                              currentSurvey?.response?.settings
                                                ?.primaryColor || '#03aae4',
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography variant="body1">
                                        {
                                          currentSurvey.response.privacySettings
                                            ?.checkboxText
                                        }
                                      </Typography>
                                    }
                                  />
                                </FormControl>

                                {/* Botones */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '1em',
                                    marginTop: '1.5em',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={!privacyAccepted}
                                    sx={{
                                      backgroundColor:
                                        currentSurvey?.response?.settings
                                          ?.primaryColor || '#03aae4',
                                      color:
                                        currentSurvey?.response?.settings
                                          ?.secondaryColor || '#fff',
                                      '&:hover': {
                                        backgroundColor:
                                          currentSurvey?.response?.settings
                                            ?.secondaryColor || '#0288d1',
                                        color:
                                          currentSurvey?.response?.settings
                                            ?.primaryColor || '#fff',
                                      },
                                      '&.Mui-disabled': {
                                        backgroundColor: '#ccc',
                                        color: '#666',
                                      },
                                    }}
                                  >
                                    {
                                      currentSurvey.response.privacySettings
                                        ?.buttonAceotText
                                    }
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    onClick={handleDeclinePrivacyClick}
                                    disabled={!privacyAccepted}
                                    sx={{
                                      borderColor: '#d32f2f',
                                      color: '#d32f2f',
                                      '&:hover': {
                                        borderColor: '#b71c1c',
                                        backgroundColor:
                                          'rgba(211, 47, 47, 0.04)',
                                      },
                                    }}
                                  >
                                    {
                                      currentSurvey.response.privacySettings
                                        ?.buttonDeclineText
                                    }
                                  </Button>
                                </Box>
                              </Box>

                              {/* Modal de confirmación */}
                              <Dialog
                                open={openDeclineModal}
                                onClose={handleCancelDecline}
                                aria-labelledby="alert-dialog-title"
                                aria-describedby="alert-dialog-description"
                                PaperProps={{
                                  sx: {
                                    fontFamily:
                                      currentSurvey?.response?.fontFamily ||
                                      'Roboto',
                                    '& *': {
                                      fontFamily: 'inherit !important',
                                    },
                                  },
                                }}
                              >
                                <DialogTitle id="alert-dialog-title">
                                  {
                                    currentSurvey?.response?.privacySettings
                                      ?.declineMessage?.title
                                  }
                                </DialogTitle>
                                <DialogContent>
                                  <DialogContentText id="alert-dialog-description">
                                    {
                                      currentSurvey?.response?.privacySettings
                                        ?.declineMessage?.message
                                    }
                                  </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                  <Button
                                    onClick={handleCancelDecline}
                                    sx={{
                                      color:
                                        currentSurvey?.response?.settings
                                          ?.primaryColor || '#03aae4',
                                    }}
                                  >
                                    {
                                      currentSurvey?.response?.privacySettings
                                        ?.declineMessage?.buttonDeclineText
                                    }
                                  </Button>
                                  <Button
                                    onClick={handleConfirmDecline}
                                    sx={{
                                      color: '#d32f2f',
                                      fontWeight: 'bold',
                                    }}
                                    autoFocus
                                  >
                                    {
                                      currentSurvey?.response?.privacySettings
                                        ?.declineMessage?.buttonAcceptText
                                    }
                                  </Button>
                                </DialogActions>
                              </Dialog>
                            </Fragment>
                          )}
                        {isIntroductionStep() && (
                          <Fragment>
                            <Box
                              sx={{
                                padding: '1em',
                                fontFamily:
                                  currentSurvey?.response?.fontFamily ||
                                  'Roboto',
                                '& *': {
                                  fontFamily: 'inherit !important',
                                },
                              }}
                            >
                              {/* Contenido de la introducción */}
                              <Box
                                sx={{
                                  padding: '1.5em',
                                  backgroundColor: '#f9fafb',
                                  borderRadius: '8px',
                                  marginBottom: '1.5em',
                                  border: '1px solid #e5e7eb',
                                  lineHeight: 1.6,

                                  // 👇 LÍMITE + SCROLL
                                  maxHeight: '300px', // ajusta a tu gusto
                                  overflowY: 'auto',
                                }}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    currentSurvey?.response?.settings
                                      ?.introductionText || '',
                                }}
                              />

                              {/* Botón para continuar */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  marginTop: '1.5em',
                                }}
                              >
                                <Button
                                  variant="contained"
                                  onClick={handleNext}
                                  sx={{
                                    backgroundColor:
                                      currentSurvey?.response?.settings
                                        ?.primaryColor || '#03aae4',
                                    color:
                                      currentSurvey?.response?.settings
                                        ?.secondaryColor || '#fff',
                                    '&:hover': {
                                      backgroundColor:
                                        currentSurvey?.response?.settings
                                          ?.secondaryColor || '#0288d1',
                                      color:
                                        currentSurvey?.response?.settings
                                          ?.primaryColor || '#fff',
                                    },
                                  }}
                                >
                                  Continuar
                                </Button>
                              </Box>
                            </Box>
                          </Fragment>
                        )}
                        <div style={{ padding: '2em 1em 1em 1em' }}>
                          {/* first step */}

                          {isDemographicStep() && (
                            <Fragment>
                              <SurveyForm
                                demographicData={
                                  currentSurveyDemographic &&
                                  currentSurveyDemographic.demographics
                                }
                                initialDemographicSelections={
                                  initialDemographicSelections
                                }
                                pagination={currentSurvey.response.pagination}
                                companyId={companyId}
                                nameStep={steps}
                                activeStepper={activeStep}
                                handleNextAnswer={handleNext}
                                onAnswered={(answers) =>
                                  handleAnswered(answers, activeStep)
                                }
                                primaryColor={
                                  currentSurvey?.response?.settings
                                    ?.primaryColor
                                }
                                secondaryColor={
                                  currentSurvey?.response?.settings
                                    ?.secondaryColor
                                }
                                data={pausedData}
                                fontFamily={
                                  currentSurvey?.response?.fontFamily ||
                                  'Roboto'
                                }
                                handlePreviousAnswer={handlePreviousAnswer}
                                surveyId={surveyId}
                                answerId={answerId ?? answerIdAPI ?? ''}
                                hasOwnBold={hasOwnBold}
                              />
                            </Fragment>
                          )}

                          {/* survey form */}
                          {isSurveyStep() &&
                            currentSurveyQuestion &&
                            currentSurveyQuestion.length > 0 && (
                              <SurveyForm
                                questions={currentSurveyQuestion}
                                pagination={currentSurvey.response.pagination}
                                descriptionSurvey={
                                  currentSurvey.response.descriptionSurvey
                                }
                                handleNextAnswer={handleNext}
                                nameStep={steps}
                                onAnswered={(answers) =>
                                  handleAnswered(answers, activeStep)
                                }
                                activeStepper={activeStep}
                                primaryColor={
                                  currentSurvey?.response?.settings
                                    ?.primaryColor
                                }
                                secondaryColor={
                                  currentSurvey?.response?.settings
                                    ?.secondaryColor
                                }
                                isSaveOption={currentSurvey.response.saveOption}
                                saveSurvey={saveSurvey}
                                data={pausedData}
                                demographicData={
                                  currentSurveyDemographic &&
                                  currentSurveyDemographic.demographics
                                }
                                initialDemographicSelections={
                                  initialDemographicSelections
                                }
                                fontFamily={
                                  currentSurvey?.response?.fontFamily ||
                                  'Roboto'
                                }
                                handlePreviousAnswer={handlePreviousAnswer}
                                surveyId={surveyId}
                                answerId={answerId ?? answerIdAPI ?? ''}
                                hasOwnBold={hasOwnBold}
                              />
                            )}
                          {/* success message */}
                          {isFinalStep() && (
                            <SuccessMessage
                              isAlreadyResponse={isAlreadyResponse}
                              isView360={
                                currentSurvey &&
                                currentSurvey.response.isView360
                              }
                              surveyId={surveyId}
                              companyId={companyId}
                              token={token}
                              isPersonalLink={Boolean(answerId)}
                            />
                          )}
                        </div>

                        <Box
                          sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}
                        >
                          <Box sx={{ flex: '1 1 auto' }} />
                          {isStepOptional(activeStep) && (
                            <Button
                              color="inherit"
                              onClick={handleSkip}
                              sx={{ mr: 1 }}
                            >
                              Saltar
                            </Button>
                          )}
                          <Button
                            onClick={handleNext}
                            disabled={
                              !stepsCompleted[activeStep] ||
                              surveyStatus === 'loading'
                            }
                          >
                            {activeStep === steps.length - 2 ? '' : ''}
                          </Button>
                        </Box>
                      </React.Fragment>
                    </Fragment>
                  )}
                </Fragment>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnswerSurvey;
